import type { Capability } from '@taler/api-client'
import type { RouteRecordRaw } from 'vue-router'
import { canCreateEntries } from '@/features/entries'
import { canViewContacts } from '@/features/contacts'
import { canViewAccounts } from '@/features/accounts'
import { canViewReconciliations } from '@/features/reconciliation'
import { canViewLoans } from '@/features/loans'
import { canViewGarage } from '@/features/garage'
import { canManageMembership } from '@/features/membership'
import { canManageLedgerLifecycle } from '@/features/ledger-lifecycle'
import { canViewExports } from '@/features/exports'
import { canViewMerchants } from '@/features/merchants'
import { canManageReceiptScans } from '@/features/receipt-scans'
import { canManageImports } from '@/features/imports'
import { canViewDashboard } from '@/features/dashboard'
import { canViewRelationships } from '@/features/relationships'
import { canViewReporting } from '@/features/reporting'
import { canViewTargets } from '@/features/targets'
import { canViewForecast } from '@/features/forecast'
import { canViewTransfers } from '@/features/transfers'
import { canViewTransactions } from '@/features/transactions'
import Dashboard from '@/pages/Dashboard.vue'
import Foundation from '@/pages/Foundation.vue'
import InvitationAccept from '@/pages/InvitationAccept.vue'
import Members from '@/pages/Members.vue'
import NotProvisioned from '@/pages/NotProvisioned.vue'
import SignIn from '@/pages/SignIn.vue'
import AuthCallback from '@/pages/AuthCallback.vue'
import Transactions from '@/pages/Transactions.vue'
import NewEntries from '@/pages/NewEntries.vue'
import SplitExpense from '@/pages/SplitExpense.vue'
import AdminIdentity from '@/pages/AdminIdentity.vue'
import Contacts from '@/pages/Contacts.vue'
import ContactDetail from '@/pages/ContactDetail.vue'
import Accounts from '@/pages/Accounts.vue'
import AccountDetail from '@/pages/AccountDetail.vue'
import Reconciliations from '@/pages/Reconciliations.vue'
import Reconciliation from '@/pages/Reconciliation.vue'
import Loans from '@/pages/Loans.vue'
import LoanDetail from '@/pages/LoanDetail.vue'
import Garage from '@/pages/Garage.vue'
import Exports from '@/pages/Exports.vue'
import BackupRestore from '@/pages/BackupRestore.vue'
import LedgerLifecycle from '@/pages/LedgerLifecycle.vue'
import Merchants from '@/pages/Merchants.vue'
import ReceiptScans from '@/pages/ReceiptScans.vue'
import ReceiptScanDetail from '@/pages/ReceiptScanDetail.vue'
import Imports from '@/pages/Imports.vue'
import ImportJobDetail from '@/pages/ImportJobDetail.vue'
import Inbox from '@/pages/Inbox.vue'
import Relationships from '@/pages/Relationships.vue'
import ReportingNetWorth from '@/pages/ReportingNetWorth.vue'
import ReportingOverview from '@/pages/ReportingOverview.vue'
import ReportingIncomeExpenses from '@/pages/ReportingIncomeExpenses.vue'
import ReportingAccounts from '@/pages/ReportingAccounts.vue'
import ReportingCategories from '@/pages/ReportingCategories.vue'
import ReportingPayees from '@/pages/ReportingPayees.vue'
import ReportingTags from '@/pages/ReportingTags.vue'
import ReportingTrends from '@/pages/ReportingTrends.vue'
import ReportingPeriodRollup from '@/pages/ReportingPeriodRollup.vue'
import ReportingParentRollup from '@/pages/ReportingParentRollup.vue'
import ReportingLentTo from '@/pages/ReportingLentTo.vue'
import Notifications from '@/pages/Notifications.vue'
import NotificationPreferences from '@/pages/NotificationPreferences.vue'
import Targets from '@/pages/Targets.vue'
import Forecast from '@/pages/Forecast.vue'
import ForecastScenarioDetail from '@/pages/ForecastScenarioDetail.vue'

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Whether the active ledger's role may open this route - the same predicate the page
     * and, where the route has one, the nav item already gate on. One guard reads it. A
     * function rather than a single `Capability` so a route gated on a disjunction (view
     * transactions, say: `ENTRY_VIEW_ALL ∨ ENTRY_VIEW_OWN`) is expressible, not skipped.
     */
    access?: (can: (capability: Capability) => boolean) => boolean
    /** Routes reachable without a resolved session. */
    public?: boolean
  }
}

export const routes: RouteRecordRaw[] = [
  // Foundation proves the scaffolding works; it is not product chrome, so it neither
  // appears in navigation nor is where a signed-in user lands. It stays reachable by URL.
  { path: '/', redirect: '/dashboard' },
  { path: '/foundation', name: 'foundation', component: Foundation },
  {
    path: '/transactions',
    name: 'transactions',
    component: Transactions,
    meta: { access: canViewTransactions },
  },
  {
    path: '/entries/new',
    name: 'entries-new',
    component: NewEntries,
    meta: { access: canCreateEntries },
  },
  {
    path: '/entries/split',
    name: 'entries-split',
    component: SplitExpense,
    meta: { access: canCreateEntries },
  },
  { path: '/settings/payment-account', redirect: { name: 'accounts' } },
  {
    path: '/entries/receipt-scan',
    name: 'receipt-scan',
    component: ReceiptScans,
    meta: { access: canManageReceiptScans },
  },
  {
    path: '/entries/receipt-scan/:jobId',
    name: 'receipt-scan-detail',
    component: ReceiptScanDetail,
    meta: { access: canManageReceiptScans },
  },
  {
    path: '/imports',
    name: 'imports',
    component: Imports,
    meta: { access: canManageImports },
  },
  {
    path: '/imports/:jobId',
    name: 'import-job-detail',
    component: ImportJobDetail,
    meta: { access: canManageImports },
  },
  {
    path: '/contacts',
    name: 'contacts',
    component: Contacts,
    meta: { access: canViewContacts },
  },
  {
    path: '/contacts/:contactId',
    name: 'contact-detail',
    component: ContactDetail,
    meta: { access: canViewContacts },
  },
  { path: '/accounts', name: 'accounts', component: Accounts, meta: { access: canViewAccounts } },
  {
    path: '/accounts/:accountId',
    name: 'account-detail',
    component: AccountDetail,
    meta: { access: canViewAccounts },
  },
  {
    path: '/reconciliations',
    name: 'reconciliations',
    component: Reconciliations,
    meta: { access: canViewReconciliations },
  },
  {
    path: '/reconciliations/:id',
    name: 'reconciliation',
    component: Reconciliation,
    meta: { access: canViewReconciliations },
  },
  {
    path: '/loans',
    name: 'loans',
    component: Loans,
    meta: { access: canViewLoans },
  },
  {
    path: '/loans/:loanId',
    name: 'loan-detail',
    component: LoanDetail,
    meta: { access: canViewLoans },
  },
  {
    path: '/garage',
    name: 'garage',
    component: Garage,
    meta: { access: canViewGarage },
  },
  {
    path: '/garage/:carId',
    name: 'garage-car-detail',
    component: Garage,
    props: true,
    meta: { access: canViewGarage },
  },
  { path: '/members', name: 'members', component: Members, meta: { access: canManageMembership } },
  // No `meta.access`: `taler:identity:manage` is a principal authority, not a ledger
  // `Capability`, so it cannot be expressed through this guard's `can` predicate. The page
  // itself gates on `session.principal.value?.authorities` instead.
  { path: '/admin/identity', name: 'admin-identity', component: AdminIdentity },
  // No `meta.access`: the notification feed is gated on being signed in, not a `Capability`.
  { path: '/notifications', name: 'notifications', component: Notifications },
  // No `meta.access`: a member manages only their own notification preferences - membership
  // itself is the gate, the same reasoning `setDefaultPaymentAccount` uses.
  {
    path: '/settings/notification-preferences',
    name: 'notification-preferences',
    component: NotificationPreferences,
  },
  {
    path: '/invite/:token',
    name: 'invitation-accept',
    component: InvitationAccept,
    meta: { public: true },
  },
  { path: '/exports', name: 'exports', component: Exports, meta: { access: canViewExports } },
  // No meta.access: restoring a backup always creates and owns a brand-new ledger, so it
  // depends on being signed in only, never on a Capability against the active ledger - the
  // same reasoning `notification-preferences` uses. The export half gates its own section.
  { path: '/settings/backup-restore', name: 'backup-restore', component: BackupRestore },
  // Owner-only: disabling and restoring the active household is `LEDGER_LIFECYCLE_MANAGE`.
  {
    path: '/settings/ledger-lifecycle',
    name: 'ledger-lifecycle',
    component: LedgerLifecycle,
    meta: { access: canManageLedgerLifecycle },
  },
  { path: '/merchants', name: 'merchants', component: Merchants, meta: { access: canViewMerchants } },
  { path: '/dashboard', name: 'dashboard', component: Dashboard, meta: { access: canViewDashboard } },
  {
    path: '/relationships',
    name: 'relationships',
    component: Relationships,
    meta: { access: canViewRelationships },
  },
  { path: '/reports', redirect: { name: 'reporting-overview' } },
  {
    path: '/reports/overview',
    name: 'reporting-overview',
    component: ReportingOverview,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/income-expenses',
    name: 'reporting-income-expenses',
    component: ReportingIncomeExpenses,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/accounts',
    name: 'reporting-accounts',
    component: ReportingAccounts,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/categories',
    name: 'reporting-categories',
    component: ReportingCategories,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/payees',
    name: 'reporting-payees',
    component: ReportingPayees,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/tags',
    name: 'reporting-tags',
    component: ReportingTags,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/trends',
    name: 'reporting-trends',
    component: ReportingTrends,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/net-worth',
    name: 'reporting-net-worth',
    component: ReportingNetWorth,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/period-rollup',
    name: 'reporting-period-rollup',
    component: ReportingPeriodRollup,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/parent-rollup',
    name: 'reporting-parent-rollup',
    component: ReportingParentRollup,
    meta: { access: canViewReporting },
  },
  {
    path: '/reports/lent-to',
    name: 'reporting-lent-to',
    component: ReportingLentTo,
    meta: { access: canViewReporting },
  },
  {
    path: '/targets',
    name: 'targets',
    component: Targets,
    meta: { access: canViewTargets },
  },
  {
    path: '/forecast',
    name: 'forecast',
    component: Forecast,
    meta: { access: canViewForecast },
  },
  {
    path: '/forecast/:scenarioId',
    name: 'forecast-scenario',
    component: ForecastScenarioDetail,
    meta: { access: canViewForecast },
  },
  { path: '/transfers/inbox', name: 'transfers-inbox', component: Inbox, meta: { access: canViewTransfers } },
  { path: '/not-provisioned', name: 'not-provisioned', component: NotProvisioned, meta: { public: true } },
  { path: '/sign-in', name: 'sign-in', component: SignIn, meta: { public: true } },
  { path: '/auth/callback', name: 'auth-callback', component: AuthCallback, meta: { public: true } },
  { path: '/:pathMatch(.*)*', redirect: '/transactions' },
]
